/**
 * VISTOERA_PLATFORM_ADMIN_MARKETING_ANALYTICS_HOME_AND_TRACKING_FOUNDATION_V1
 * — Conversion Funnel (task spec §13/§58). Funnel-style bars, each
 * proportional to `visitors` (the widest, first stage) — a dependency-
 * free `<div>` bar chart, same "no charting library" precedent as every
 * other chart in this codebase.
 */
import { platformColors } from './theme'

export interface FunnelStage {
  key: string
  label: string
  value: number | null
  /** `null` when this stage's rate is not computable (task spec §11 —
   * never a fabricated conversion rate) or the value itself is
   * unavailable (task spec §28-30 — Paid/Revenue). */
  rateLabel: string | null
  available: boolean
}

export default function MarketingFunnelChart({ stages, notAvailableLabel }: { stages: FunnelStage[]; notAvailableLabel: string }) {
  const maxValue = Math.max(...stages.map((s) => s.value ?? 0), 1)
  const colors = [platformColors.primary, 'var(--pf-primary-soft-strong, #A5A8FA)', 'var(--pf-info, #67D3E8)', 'var(--pf-info-bg, #C7EEF6)']

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {stages.map((stage, i) => {
        const widthPct = stage.available && stage.value !== null ? Math.max(4, (stage.value / maxValue) * 100) : 4
        return (
          <div key={stage.key} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 84, fontSize: 12, color: platformColors.secondaryText, flexShrink: 0 }}>{stage.label}</div>
            <div style={{ flex: 1, position: 'relative', height: 34 }}>
              <div
                style={{
                  height: '100%',
                  width: `${widthPct}%`,
                  minWidth: 46,
                  borderRadius: 6,
                  background: stage.available ? colors[i % colors.length] : 'var(--pf-neutral-bg)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: stage.available ? '#fff' : platformColors.mutedText,
                  fontSize: 13,
                  fontWeight: 600,
                  transition: 'width 0.3s ease',
                }}
              >
                {stage.available ? (stage.value?.toLocaleString() ?? '—') : notAvailableLabel}
              </div>
            </div>
            {i > 0 && (
              <div style={{ width: 108, fontSize: 11, color: platformColors.mutedText, flexShrink: 0, textAlign: 'right' }}>
                {stage.rateLabel ?? '—'}
              </div>
            )}
            {i === 0 && <div style={{ width: 108, flexShrink: 0 }} />}
          </div>
        )
      })}
    </div>
  )
}
